import React, { useState, useEffect } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, Button, Stack, Chip,
  IconButton, TextField, MenuItem, Tooltip, Avatar, LinearProgress,
  Divider, Paper, Rating
} from '@mui/material';
import {
  Edit as EditIcon, Delete as DeleteIcon,
  Person as PersonIcon,
  ArrowBack as ArrowBackIcon, Email as EmailIcon, Phone as PhoneIcon,
  Business as BusinessIcon, LocationOn as LocationIcon,
  Star as StarIcon
} from '@mui/icons-material';
import { useLanguage } from '../../Commun/contexts/LanguageContext';
import GenericPage, { useDefaultViewMode } from '../../Commun/components/GenericPage';
import GenericTable from '../../Commun/components/GenericTable';
import { blobticMessage } from '../../Commun/components/BlobticMessage';
import { crmApi } from '../services/crmApi';
import { CrmContact, ContactType, CommPref, CrmOrganisation } from '../types/crm';

const emptyContact = (): Partial<CrmContact> => ({
  firstName: '',
  lastName: '',
  company: '',
  role: '',
  email: '',
  phone: '',
  mobile: '',
  address: '',
  city: '',
  type: 'CLIENT',
  influence: 3,
  prefComm: 'EMAIL',
  prefHours: '',
  prefLang: 'FR'
});

export const ContactsPage: React.FC = () => {
  const { t } = useLanguage();

  const [view, setView] = useState<'list' | 'form'>('list');
  const [viewMode, setViewMode] = useDefaultViewMode();
  const [contactsList, setContactsList] = useState<CrmContact[]>([]);
  const [organisationsList, setOrganisationsList] = useState<CrmOrganisation[]>([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<Partial<CrmContact>>(emptyContact());
  const [isEditing, setIsEditing] = useState(false);

  const loadData = async () => {
    setLoading(true);
    try {
      const [cData, oData] = await Promise.all([
        crmApi.getContacts(),
        crmApi.getOrganisations()
      ]);
      setContactsList(cData);
      setOrganisationsList(oData);
    } catch (err) {
      console.error('Error loading CRM contacts:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const openNew = () => {
    setFormData(emptyContact());
    setIsEditing(false);
    setView('form');
  };

  const openEdit = (contact: CrmContact) => {
    setFormData({ ...contact });
    setIsEditing(true);
    setView('form');
  };

  const backToList = () => {
    setView('list');
  };

  const handleDelete = async (contact: CrmContact) => {
    const contactId = (contact.id || contact.CCT_IDF)!;
    const confirmed = await blobticMessage.confirm({
      message: t('crm.contacts.confirmDelete', 'Êtes-vous sûr de vouloir supprimer ce contact ?'),
      title: t('common.confirm', 'Confirmation')
    });

    if (confirmed) {
      const res = await crmApi.deleteContact(contactId);
      if (res.success) {
        blobticMessage.success(t('crm.contacts.deleteSuccess', 'Contact supprimé avec succès.'));
        loadData();
      } else {
        blobticMessage.error(res.message || t('common.error', 'Erreur lors de la suppression.'));
      }
    }
  };

  const handleValidate = (): string | null => {
    if (!formData.lastName?.trim()) {
      return t('crm.contacts.lastNameRequired', 'Le nom de famille est requis.');
    }
    return null;
  };

  const handleDataChange = () => {
    backToList();
    loadData();
  };

  if (view === 'form') {
    const formPayload = {
      ...formData,
      CCT_IDF: formData.id || formData.CCT_IDF,
      CCT_CODE: formData.code || formData.CCT_CODE,
      CCT_FIRSTNAME: formData.firstName,
      CCT_LASTNAME: formData.lastName,
      COR_IDF: formData.organisationId || formData.COR_IDF,
      CCT_COMPANY: formData.company,
      CCT_ROLE: formData.role,
      CCT_EMAIL: formData.email,
      CCT_PHONE: formData.phone,
      CCT_MOBILE: formData.mobile,
      CCT_ADDRESS: formData.address,
      CCT_CITY: formData.city,
      CCT_TYPE: formData.type,
      CCT_INFLUENCE: formData.influence,
      CCT_PREF_COMM: formData.prefComm,
      CCT_PREF_HOURS: formData.prefHours,
      CCT_PREF_LANG: formData.prefLang,
    };

    return (
      <GenericPage<any>
        title={isEditing ? t('crm.contacts.editTitle', 'Modifier le Contact / Décideur') : t('crm.contacts.newTitle', 'Nouveau Contact & Décideur')}
        icon={<PersonIcon />}
        footerEnabled={true}
        footerInfo={formData.last_updt ? { lastUpdate: formData.last_updt, userLib: formData.user_lib } : undefined}
        additionalActions={
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={backToList}
          >
            {t('common.backToList', 'Retour à la liste')}
          </Button>
        }
        crudConfig={{ entityKey: 'Blobtic_CRM,crm_contact', primaryKey: 'CCT_IDF' }}
        data={formPayload}
        onDataChange={handleDataChange}
        onValidate={handleValidate}
        onClear={() => setFormData(emptyContact())}
      >
        <Paper sx={{ p: 3, borderRadius: 2 }}>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            {t('crm.contacts.detailsSection', 'Informations Personnelles & Professionnelles')}
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={2.5}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('crm.contacts.field.firstName', 'Prénom')}
                value={formData.firstName || ''}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                size="small"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('crm.contacts.field.lastName', 'Nom *')}
                value={formData.lastName || ''}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                required
                size="small"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label={t('crm.contacts.field.organisation', 'Organisation Rattachée (BET / Promo / MO)')}
                value={formData.organisationId || ''}
                onChange={(e) => {
                  const orgId = Number(e.target.value);
                  const selectedOrg = organisationsList.find(o => (o.id || o.COR_IDF) === orgId);
                  setFormData({
                    ...formData,
                    organisationId: orgId || undefined,
                    company: selectedOrg ? selectedOrg.name : formData.company
                  });
                }}
                size="small"
              >
                <MenuItem value="">-- {t('crm.contacts.noOrgDirect', 'Aucune / Entreprise directe')} --</MenuItem>
                {organisationsList.map((org) => (
                  <MenuItem key={org.id || org.COR_IDF} value={org.id || org.COR_IDF}>
                    {org.name} ({org.type})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('crm.contacts.field.company', 'Entreprise / Société')}
                value={formData.company || ''}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                size="small"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('crm.contacts.field.role', 'Fonction / Poste')}
                value={formData.role || ''}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                size="small"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, height: '100%' }}>
                <Typography variant="body2" color="text.secondary">
                  {t('crm.contacts.influence', 'Niveau d\'Influence Décisionnelle :')}
                </Typography>
                <Rating
                  value={formData.influence || 3}
                  max={5}
                  onChange={(_, val) => setFormData({ ...formData, influence: val || 3 })}
                />
                <Typography variant="caption" fontWeight={700}>
                  ({formData.influence || 3}/5)
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="email"
                label={t('crm.contacts.field.email', 'Email')}
                value={formData.email || ''}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                size="small"
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label={t('crm.contacts.field.phone', 'Téléphone')}
                value={formData.phone || ''}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                size="small"
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label={t('crm.contacts.field.mobile', 'Mobile')}
                value={formData.mobile || ''}
                onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                size="small"
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                select
                label={t('crm.contacts.field.prefComm', 'Canal de Communication Préféré')}
                value={formData.prefComm || 'EMAIL'}
                onChange={(e) => setFormData({ ...formData, prefComm: e.target.value as CommPref })}
                size="small"
              >
                <MenuItem value="EMAIL">{t('crm.contacts.prefEmail', 'Email')}</MenuItem>
                <MenuItem value="PHONE">{t('crm.contacts.prefPhone', 'Téléphone')}</MenuItem>
                <MenuItem value="MEETING">{t('crm.contacts.prefMeeting', 'Rendez-vous physique')}</MenuItem>
                <MenuItem value="WHATSAPP">{t('crm.contacts.prefWhatsapp', 'WhatsApp / SMS')}</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label={t('crm.contacts.field.prefHours', 'Horaires Préférés')}
                value={formData.prefHours || ''}
                onChange={(e) => setFormData({ ...formData, prefHours: e.target.value })}
                placeholder={t('crm.contacts.hoursPlaceholder', 'Ex: Matinées 9h-12h')}
                size="small"
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                select
                label={t('crm.contacts.field.prefLang', 'Langue Préférée')}
                value={formData.prefLang || 'FR'}
                onChange={(e) => setFormData({ ...formData, prefLang: e.target.value })}
                size="small"
              >
                <MenuItem value="FR">{t('common.french', 'Français')}</MenuItem>
                <MenuItem value="AR">{t('common.arabic', 'Arabe')}</MenuItem>
                <MenuItem value="EN">{t('common.english', 'Anglais')}</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                select
                label={t('crm.contacts.field.type', 'Type de contact')}
                value={formData.type || 'CLIENT'}
                onChange={(e) => setFormData({ ...formData, type: e.target.value as ContactType })}
                size="small"
              >
                <MenuItem value="CLIENT">{t('crm.type.client', 'Client')}</MenuItem>
                <MenuItem value="PROSPECT">{t('crm.type.prospect', 'Prospect')}</MenuItem>
                <MenuItem value="PARTNER">{t('crm.type.partner', 'Partenaire')}</MenuItem>
                <MenuItem value="VENDOR">{t('crm.type.vendor', 'Fournisseur')}</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label={t('crm.contacts.field.city', 'Ville')}
                value={formData.city || ''}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                size="small"
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label={t('crm.contacts.field.address', 'Adresse')}
                value={formData.address || ''}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                size="small"
              />
            </Grid>
          </Grid>
        </Paper>
      </GenericPage>
    );
  }

  // List View
  return (
    <GenericPage
      title={t('crm.contacts.title', 'Contacts & Décideurs Clés')}
      icon={<PersonIcon />}
      footerEnabled={false}
      viewMode={viewMode}
      onViewModeChange={setViewMode}
      onNew={openNew}
    >
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {viewMode === 'grid' ? (
        <Grid container spacing={2.5}>
          {contactsList.map((contact) => {
            const contactId = contact.id || contact.CCT_IDF;
            return (
              <Grid item xs={12} sm={6} md={4} key={contactId}>
                <Card
                  variant="outlined"
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    borderRadius: 2.5,
                    transition: 'all 0.25s ease-in-out',
                    '&:hover': {
                      boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                      borderColor: 'primary.main',
                      transform: 'translateY(-3px)'
                    }
                  }}
                >
                  <CardContent sx={{ p: 2.5 }}>
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                      <Avatar sx={{ bgcolor: 'primary.main', width: 44, height: 44 }}>
                        {contact.firstName?.[0] || contact.lastName?.[0] || 'C'}
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle1" fontWeight={700} noWrap>
                          {contact.firstName} {contact.lastName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {contact.role || t('crm.contacts.noRole', 'Rôle non spécifié')}
                        </Typography>
                      </Box>
                      <Chip
                        label={t(contact.type || '', contact.type)}
                        color={contact.type === 'CLIENT' ? 'primary' : 'default'}
                        size="small"
                        sx={{ fontWeight: 600 }}
                      />
                    </Stack>

                    <Divider sx={{ my: 1.5 }} />

                    <Stack spacing={1}>
                      {contact.organisationName ? (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <BusinessIcon fontSize="inherit" color="action" />
                          <Typography variant="body2" color="primary.main" fontWeight={600} noWrap>
                            🏢 {contact.organisationName}
                          </Typography>
                        </Stack>
                      ) : contact.company ? (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <BusinessIcon fontSize="inherit" color="action" />
                          <Typography variant="body2" color="text.secondary" noWrap>
                            {contact.company}
                          </Typography>
                        </Stack>
                      ) : null}

                      {/* Niveau d'influence décisionnelle */}
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="caption" color="text.secondary">{t('crm.contacts.influence', 'Influence')} :</Typography>
                        <Rating value={contact.influence || 3} max={5} size="small" readOnly />
                      </Stack>

                      {contact.email && (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <EmailIcon fontSize="inherit" color="action" />
                          <Typography variant="body2" color="text.secondary" noWrap>
                            {contact.email}
                          </Typography>
                        </Stack>
                      )}

                      {contact.phone && (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <PhoneIcon fontSize="inherit" color="action" />
                          <Typography variant="body2" color="text.secondary">
                            {contact.phone}
                          </Typography>
                        </Stack>
                      )}

                      {contact.prefHours && (
                        <Typography variant="caption" color="text.secondary">
                          🕒 {t('crm.contacts.dispo', 'Dispo')} : {contact.prefHours} ({contact.prefComm || 'EMAIL'})
                        </Typography>
                      )}
                    </Stack>
                  </CardContent>

                  <Box sx={{ px: 2.5, py: 1.5, bgcolor: 'background.default', borderTop: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" color="text.secondary">
                      {contact.code}
                    </Typography>
                    <Stack direction="row" spacing={0.5}>
                      <Tooltip title={t('common.edit', 'Modifier')}>
                        <IconButton size="small" color="primary" onClick={() => openEdit(contact)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('common.delete', 'Supprimer')}>
                        <IconButton size="small" color="error" onClick={() => handleDelete(contact)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Box>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      ) : (
        <GenericTable
          columns={[
            { id: 'code', label: t('common.code', 'Code'), width: 110 },
            {
              id: 'fullName',
              label: t('crm.contacts.field.fullName', 'Nom Complet'),
              minWidth: 180,
              format: (_, row) => `${row.firstName || ''} ${row.lastName || ''}`.trim()
            },
            { id: 'organisationName', label: t('crm.contacts.field.organisation', 'Organisation'), width: 180 },
            { id: 'role', label: t('crm.contacts.field.role', 'Fonction'), width: 140 },
            {
              id: 'influence',
              label: t('crm.contacts.influence', 'Influence'),
              width: 110,
              format: (val) => `${val || 3}/5 ⭐`
            },
            { id: 'email', label: t('crm.contacts.field.email', 'Email'), width: 180 },
            { id: 'phone', label: t('crm.contacts.field.phone', 'Téléphone'), width: 130 },
            {
              id: 'type',
              label: t('crm.contacts.field.type', 'Type'),
              width: 110,
              chip: true,
              chipColorMap: {
                CLIENT: 'primary',
                PROSPECT: 'default',
                PARTNER: 'info',
                VENDOR: 'warning'
              }
            },
          ]}
          data={contactsList}
          onEdit={openEdit}
          onDelete={handleDelete}
          searchPlaceholder={t('crm.contacts.searchPlaceholder', 'Rechercher un contact, organisation, email...')}
          filterColumns={['code', 'firstName', 'lastName', 'organisationName', 'company', 'email', 'phone', 'role', 'type']}
          maxHeight="calc(100vh - 270px)"
        />
      )}
    </GenericPage>
  );
};

export default ContactsPage;
